import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import classNames from "classnames";
import Bugsnag from "@bugsnag/js";
import ahoy from "@lib/ahoy";
import { useCurrentUser, useSizeReferenceDisplay } from "@hooks/use-current-user";
import useCustomizableCloset, { useAddSelectedItemToCloset } from "@hooks/use-customizable-closet";
import sizeTypes from "@utils/constants/sizeTypes";
import sortSizes from "@utils/sortSizes";
import { showWarningToast } from "@utils/toasts/showToasts";
import { productAddedEvent } from "@utils/events/product-events";
import InputButtonsSameSize from "../InputButtonsSameSize";
import { animateItemCounter } from "@utils/animations";
import CustomLoader from "../CustomLoader";

function QuickAddToClosetOffCanvasVariants({
  onHide,
  productWithVariants,
  dataFromAlgolia,
  isDoubleSize,
}) {
  const tCommon = useTranslations("common");
  const tProduct = useTranslations("product");

  const user = useCurrentUser();
  const currentCloset = useCustomizableCloset();
  const addSelectedItemToCloset = useAddSelectedItemToCloset();
  const sizeReference = useSizeReferenceDisplay();

  const [mainSizeSelected, setMainSizeSelected] = useState(null);
  const [sizeInseamSelected, setSizeInseamSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  function customVariant(variant) {
    if (!variant) return null;

    let mainSizeValue;
    let sizeInseamValue;

    if (variant.size && variant.size.value.charAt(2) === "x") {
      [mainSizeValue, sizeInseamValue] = variant.size.value.split("x");
    } else {
      mainSizeValue = variant.size ? variant.size.value : tProduct("unique_size");
    }

    return {
      ...variant,
      mainSize: mainSizeValue,
      sizeInseam: sizeInseamValue,
    };
  }

  const variantsList =
    productWithVariants?.variants?.map((variant) => customVariant(variant)) || [];
  const mainSizeFullList = variantsList.map((variant) => variant.mainSize);
  const mainSizeList = [...new Set(mainSizeFullList)];

  const sizeInseamFullList = variantsList.map((variant) => variant.sizeInseam);
  const sizeInseamList = [...new Set(sizeInseamFullList.filter(Boolean))];

  const [sizeInseamListAvailable, setSizeInseamListAvailable] = useState(sizeInseamList || []);

  useEffect(() => {
    setSizeInseamListAvailable(sizeInseamList);
    setMainSizeSelected(null);
  }, [productWithVariants, onHide]);

  function isMainSizeUnavailable(mainSize) {
    return !variantsList.some((el) => el.mainSize === mainSize && el.available === true);
  }

  function handleClosetError(err, variantSelectedId) {
    const error = err.response.data;
    const errorCode = error.code ? error.code : "unknown";
    const errorTitle = tCommon(`customize_closet.errors.${errorCode}.title`);
    const errorMessage = tCommon(`customize_closet.errors.${errorCode}.message`);

    showWarningToast(errorTitle, errorMessage);

    ahoy("drawerError", {
      closetId: currentCloset?.id,
      variantSelectedId,
    });
    Bugsnag.notify(new Error("Failed to add selected item to Closet"), (event) => {
      event.context = "DrawerError";
      event.severity = "info";
      event.setUser(user);
      event.addMetadata("params", {
        closetId: currentCloset?.id,
        variantSelectedId,
      });
    });
  }

  function sendProductAddedEvent() {
    const dataToSend = {
      index: dataFromAlgolia?.algolia?.index_name,
      objectID: dataFromAlgolia?.objectID,
      queryID: dataFromAlgolia.algolia?.query_id,
    };

    productAddedEvent(dataToSend, "productAddedFromCatalog");
  }

  async function quickAddToCloset(variantId) {
    setLoading(true);

    try {
      await addSelectedItemToCloset(variantId);

      animateItemCounter(".drawer-icon-counter");
      sendProductAddedEvent(variantId);
    } catch (err) {
      handleClosetError(err, variantId);
    } finally {
      onHide();
      setMainSizeSelected(null);
      setSizeInseamSelected(null);

      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }

  function handleChangeHips(value) {
    const variant = variantsList.find((el) => el.mainSize === value);

    if (variant.sizeInseam) {
      const filteredVariants = variantsList
        .filter((el) => el.mainSize === value)
        .map((v) => v.sizeInseam);

      setMainSizeSelected(value);
      setSizeInseamListAvailable(filteredVariants);
    } else {
      if (!variant.available) return;

      setMainSizeSelected(value);
      quickAddToCloset(variant.id);
    }
  }

  function handleChangeInseam(sizeInseam) {
    if (!mainSizeSelected) return;

    const variant = variantsList.find(
      (el) => el.sizeInseam === sizeInseam && el.mainSize === mainSizeSelected,
    );

    setSizeInseamSelected(variant);
    quickAddToCloset(variant.id);
  }

  function isSizeInseamUnavailable(sizeInseam) {
    const variant = variantsList.find(
      (el) => el.sizeInseam === sizeInseam && el.mainSize === mainSizeSelected,
    );

    return variant && !variant.available;
  }

  // translateVariantSize utils function isn't used here because for the size_tu & size_multiple case,
  // unique_size_button_variant ("Taille unique") already includes "Taille", unlike unique_size ("unique") used in the utils function which only returns "Unique".
  function translateSize(size) {
    switch (productWithVariants.size_type) {
      case sizeTypes.size_tu:
      case sizeTypes.size_multiple:
        return tCommon("unique_size_button_variant");
      case sizeTypes.size_fr:
        return tCommon(
          `size_display_converter.${productWithVariants.size_type}.${sizeReference}.${size}`,
        );
      default:
        return tCommon(`size_display_converter.${productWithVariants.size_type}.${size}`);
    }
  }

  const sizeReferenceMention =
    sizeTypes[productWithVariants.size_type] === sizeTypes.size_fr ? sizeReference : null;

  function renderSizeMultipleReferenceLabel() {
    if (productWithVariants.size_type !== sizeTypes.size_multiple) return null;

    const [firstSize, lastSize] = productWithVariants.variants[0].size.value.split("~");

    return (
      <p className="font-size-sm">
        {tCommon("quick_add_to_closet.offcanvas.size_reference_label.fits_from_to", {
          firstSize,
          lastSize,
        })}
      </p>
    );
  }

  const productUnavailable = productWithVariants.variants?.every((variant) => !variant.available);

  function errorMessage() {
    if (!productUnavailable) return null;

    return (
      <p className="font-size-sm text-primary mb-0 mt-3">
        {tCommon("quick_add_to_closet.offcanvas.product_unavailable")}
      </p>
    );
  }

  if (!productWithVariants.id) return null;

  return (
    <div
      className={classNames(
        "quick-add-to-closet-offcanvas-content d-flex px-3 flex-column",
        isDoubleSize ? "py-2" : "py-4",
      )}
    >
      {loading ? (
        <div className="text-light">
          <CustomLoader
            variant="SpinnerMiddleGray"
            style={{ width: "32px" }}
          />
        </div>
      ) : (
        <div>
          {renderSizeMultipleReferenceLabel()}

          {sizeInseamList.length > 0 && <p>{tCommon("quick_add_to_closet.offcanvas.size_hips")}</p>}

          <InputButtonsSameSize
            size={isDoubleSize && "sm"}
            containerClassName="d-flex align-items-center flex-wrap gap-2"
            options={sortSizes(mainSizeList)?.map((mainSize) => ({
              title: translateSize(mainSize),
              subtitle: sizeReferenceMention,
              value: mainSize,
              disabled: isMainSizeUnavailable(mainSize),
            }))}
            selectedValues={[mainSizeSelected]}
            onClick={(newMainSize) => handleChangeHips(newMainSize)}
          />

          {sizeInseamList.length > 0 && (
            <div>
              <p className="my-3">{tCommon("quick_add_to_closet.offcanvas.size_inseam")}</p>

              <InputButtonsSameSize
                size={isDoubleSize && "sm"}
                containerClassName="d-flex align-items-center flex-wrap gap-2"
                options={sortSizes(sizeInseamListAvailable)?.map((sizeInseam) => ({
                  title: translateSize(sizeInseam),
                  value: sizeInseam,
                  disabled: isSizeInseamUnavailable(sizeInseam),
                }))}
                selectedValues={[sizeInseamSelected]}
                onClick={(newSizeInseam) => handleChangeInseam(newSizeInseam)}
              />
            </div>
          )}

          {errorMessage()}
        </div>
      )}
    </div>
  );
}

export default QuickAddToClosetOffCanvasVariants;
